"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import Button from "@/components/ui/button";
import { Drawer, DrawerBody, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { ACCESS_ADDITIONAL_TUBE_HANDLING, ACCESS_FILL_NOTE, ACCESS_LABELING, ACCESS_ORDER_OF_DRAW, ACCESS_SHIPPING, type AccessGuideStep, type AccessTube } from "@/lib/access-collection-guide";
import type { AccessCollectionInstructions } from "@/types/lab";
import { labQueryOptions } from "@/api/provider/lab/lab-query";
interface AccessCollectionGuideDrawerProps {
  orderId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
function Section(t0) {
  const $ = _c(5);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <h3 className="text-body-strong text-foreground">{title}</h3>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <section className="space-y-3 border-t border-border pt-5 first:border-t-0 first:pt-0">{t1}{children}</section>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function Steps(t0) {
  const $ = _c(4);
  const {
    steps
  } = t0;
  let t1;
  if ($[0] !== steps) {
    t1 = steps.map(_temp2);
    $[0] = steps;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <ul className="list-disc space-y-1 pl-5 text-body text-foreground-secondary">{t1}</ul>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp2(step, index) {
  return <li key={index}>{step.label && <span className="font-medium text-foreground">{step.text ? `${step.label}: ` : step.label}</span>}<span>{step.text}</span>{step.substeps && <ul className="mt-1 list-[circle] space-y-1 pl-5">{step.substeps.map(_temp)}</ul>}</li>;
}
function _temp(substep) {
  return <li key={substep}>{substep}</li>;
}
function TubeRows(t0) {
  const $ = _c(7);
  const {
    tubes,
    numbered
  } = t0;
  let t1;
  if ($[0] !== numbered || $[1] !== tubes) {
    let t2;
    if ($[3] !== numbered) {
      t2 = (tube, index) => <li key={tube.name} className="flex gap-3"><span aria-hidden={true} className="mt-1 size-3 shrink-0 rounded-full ring-1 ring-border" style={{
          backgroundColor: tube.capColor
        }} /><div className="min-w-0 space-y-1"><p className="text-body-strong text-foreground">{numbered ? `${index + 1}. ${tube.name}` : tube.name}</p><Steps steps={tube.steps} /></div></li>;
      $[3] = numbered;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = tubes.map(t2);
    $[0] = numbered;
    $[1] = tubes;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <ol className="space-y-4">{t1}</ol>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
export function AccessCollectionGuide(t0) {
  const $ = _c(9);
  const {
    instructions
  } = t0;
  let t1;
  if ($[0] !== instructions.tests) {
    t1 = <Section title="For this order">{instructions.tests.length > 0 ? <div className="space-y-4">{instructions.tests.map(_temp3)}</div> : <p className="text-body text-foreground-secondary">No tests on this order have tube notes. Follow the order of draw below.</p>}</Section>;
    $[0] = instructions.tests;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Section title="Order of draw and tube handling"><p className="text-caption text-foreground-secondary">{ACCESS_FILL_NOTE}</p><TubeRows tubes={ACCESS_ORDER_OF_DRAW} numbered={true} /><p className="pt-2 text-caption-strong text-foreground-secondary">Additional tube handling</p><TubeRows tubes={ACCESS_ADDITIONAL_TUBE_HANDLING} /></Section>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Section title="Labeling"><Steps steps={ACCESS_LABELING} /></Section>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== instructions.packagingNotes) {
    t4 = <Section title="Shipping">{instructions.packagingNotes ? <><p className="text-caption text-foreground-secondary">Your clinic's packaging and shipping steps.</p><p className="whitespace-pre-line text-body text-foreground-secondary">{instructions.packagingNotes}</p></> : <Steps steps={ACCESS_SHIPPING} />}</Section>;
    $[4] = instructions.packagingNotes;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t1 || $[7] !== t4) {
    t5 = <div className="space-y-5">{t1}{t2}{t3}{t4}</div>;
    $[6] = t1;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
function _temp3(test) {
  return <div key={test.code} className="space-y-1"><p className="text-body-strong text-foreground"><span>{test.name}</span><span className="ml-2 font-mono text-caption text-foreground-muted">{test.code}</span></p><p className="whitespace-pre-line text-body text-foreground-secondary">{test.instructions}</p></div>;
}
export function AccessCollectionGuideDrawer(t0) {
  const $ = _c(12);
  const {
    orderId,
    open,
    onOpenChange
  } = t0;
  let t1;
  if ($[0] !== orderId) {
    t1 = labQueryOptions.accessCollectionInstructions(orderId);
    $[0] = orderId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== open || $[3] !== t1) {
    t2 = {
      ...t1,
      enabled: open
    };
    $[2] = open;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const instructions = useQuery(t2);
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <DrawerHeader><DrawerTitle>Collection instructions</DrawerTitle><DrawerDescription>How to draw, label and ship this on-site Access collection.</DrawerDescription></DrawerHeader>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== instructions) {
    t4 = <DrawerContent className="!w-full !max-w-xl">{t3}<DrawerBody>{instructions.data ? <AccessCollectionGuide instructions={instructions.data} /> : instructions.isError ? <div className="space-y-3"><p className="text-body text-status-error-text">Couldn't load the collection instructions.</p><Button variant="light" size="sm" onClick={() => instructions.refetch()} isLoading={instructions.isFetching}>Try again</Button></div> : <div className="animate-pulse space-y-4"><div className="h-5 w-40 rounded bg-muted" /><div className="h-16 rounded bg-muted" /><div className="h-5 w-56 rounded bg-muted" /><div className="h-32 rounded bg-muted" /></div>}</DrawerBody></DrawerContent>;
    $[6] = instructions;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== onOpenChange || $[9] !== open || $[10] !== t4) {
    t5 = <Drawer open={open} onOpenChange={onOpenChange}>{t4}</Drawer>;
    $[8] = onOpenChange;
    $[9] = open;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}